import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { 
  GraduationCap, 
  Calendar, 
  Clock, 
  Shield, 
  Car, 
  Users, 
  Phone, 
  MapPin,
  CheckCircle,
  Star,
  Timer,
  ArrowRight
} from "lucide-react";
import ServiceFAQ from "@/components/ServiceFAQ";
import InstantQuoteCTA from "@/components/InstantQuoteCTA";

// BMT Graduation dates for 2025 (Fridays)
const upcomingGraduations = [
  { date: new Date('2025-12-05'), week: 'Week 51' },
  { date: new Date('2025-12-12'), week: 'Week 52' },
  { date: new Date('2025-12-19'), week: 'Week 53' },
  { date: new Date('2026-01-03'), week: 'Week 1' },
  { date: new Date('2026-01-10'), week: 'Week 2' },
  { date: new Date('2026-01-17'), week: 'Week 3' },
  { date: new Date('2026-01-24'), week: 'Week 4' },
  { date: new Date('2026-01-31'), week: 'Week 5' },
];

const BMTGraduation = () => {
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [nextGraduation, setNextGraduation] = useState<typeof upcomingGraduations[0] | null>(null);

  useEffect(() => {
    const calculateCountdown = () => {
      const now = new Date();
      const upcoming = upcomingGraduations.find(g => g.date > now);
      
      if (upcoming) {
        setNextGraduation(upcoming);
        const diff = upcoming.date.getTime() - now.getTime();
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        setCountdown({ days, hours, minutes, seconds });
      }
    };

    calculateCountdown();
    const interval = setInterval(calculateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);
  const faqs = [
    {
      question: "How early should I book transportation for BMT Graduation?",
      answer: "We recommend booking at least 2-3 weeks in advance. BMT Graduation weekends are extremely busy in San Antonio, and transportation services book up quickly. Early booking ensures availability and allows time for security clearance processing."
    },
    {
      question: "Can you pick us up directly on Lackland AFB?",
      answer: "Yes! Rick's Black Car Service has verified base access credentials (DBIDS) that allow our chauffeurs to enter Lackland AFB without a sponsor. We can drop you off and pick you up directly at the ceremony location."
    },
    {
      question: "What information do you need from passengers for base entry?",
      answer: "We require the full legal name and date of birth for all passengers at the time of booking. All passengers over 16 must also have a valid government-issued photo ID (Driver's License, State ID, or Passport) ready to present at the gate."
    },
    {
      question: "What happens if the graduation schedule changes?",
      answer: "We monitor official Lackland AFB graduation schedules and will contact you if any changes occur. Our flexible service allows for schedule adjustments—just contact us as soon as you're notified of any changes."
    },
    {
      question: "Can you accommodate large family groups?",
      answer: "Absolutely! Our Audi Q7 luxury SUV seats up to 6 passengers. For larger groups, we can arrange multiple vehicles. Contact us for group rates and coordinated arrivals."
    },
    {
      question: "Do your rates include gratuity?",
      answer: "Yes, all our pricing for BMT Graduation transportation includes 20% gratuity. The quote you receive is the total price you pay—no hidden fees or surprises. Use our Instant Quote Calculator for exact pricing."
    }
  ];

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "BMT Graduation Transportation Service",
    "description": "Professional luxury transportation for Air Force BMT Graduation ceremonies at Lackland AFB, San Antonio. Verified base access, DBIDS-credentialed chauffeurs.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Rick's Black Car Service",
      "telephone": "+1-210-296-7409",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "San Antonio",
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "Place",
      "name": "Lackland Air Force Base, San Antonio, TX"
    },
    "serviceType": "BMT Graduation Transportation"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://ricksblackcarservicesantonio.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://ricksblackcarservicesantonio.com/services"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Military Transportation",
        "item": "https://ricksblackcarservicesantonio.com/jbsa-military-transportation"
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": "BMT Graduation Transportation",
        "item": "https://ricksblackcarservicesantonio.com/bmt-graduation"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>BMT Graduation Transportation | Lackland AFB San Antonio</title>
        <meta 
          name="description" 
          content="Professional BMT Graduation transportation at Lackland AFB. Verified base access, DBIDS-credentialed chauffeurs. Book your Air Force graduation ride!" 
        />
        <meta name="keywords" content="BMT graduation transportation, Lackland AFB car service, Air Force graduation San Antonio, military base access transportation" />
        <link rel="canonical" href="https://ricksblackcarservicesantonio.com/bmt-graduation/" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Navbar />
      
      <main className="min-h-screen bg-background pt-20">
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url('/lovable-uploads/bmt-graduation-hero.jpg')`,
              backgroundPosition: 'center'
            }}
          />
          <div className="relative z-10 container mx-auto px-4 text-center">
            <Breadcrumb 
              items={[
                { name: "Services", url: "/services" },
                { name: "Military Transportation", url: "/services/military-transportation" },
                { name: "BMT Graduation", url: "/bmt-graduation" }
              ]} 
            />
            <div className="flex justify-center mb-6">
              <div className="bg-primary/20 p-4 rounded-full">
                <GraduationCap className="h-16 w-16 text-primary" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              BMT Graduation Transportation
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto">
              Celebrate your Airman's achievement with luxury transportation to Lackland AFB
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg">
                <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Graduation Transportation</a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg bg-background/10 border-white text-white hover:bg-white hover:text-foreground">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-5 w-5" />
                  (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-8 bg-card border-y border-border">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="flex flex-col items-center">
                <Shield className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">Verified Base Access</span>
              </div>
              <div className="flex flex-col items-center">
                <CheckCircle className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">DBIDS Credentialed</span>
              </div>
              <div className="flex flex-col items-center">
                <Car className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">Luxury Audi Fleet</span>
              </div>
              <div className="flex flex-col items-center">
                <Star className="h-8 w-8 text-primary mb-2" />
                <span className="text-sm font-medium">5-Star Service</span>
              </div>
            </div>
          </div>
        </section>

        {/* BMT Graduation Countdown */}
        <section className="py-16 bg-gradient-to-b from-background to-card">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <div className="flex justify-center mb-4">
                <div className="bg-primary/10 p-3 rounded-full">
                  <Timer className="h-8 w-8 text-primary" />
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-2">Next BMT Graduation Countdown</h2>
              {nextGraduation && (
                <p className="text-muted-foreground">
                  {nextGraduation.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} ({nextGraduation.week})
                </p>
              )}
            </div>

            {/* Countdown Timer */}
            <div className="flex justify-center gap-4 md:gap-8 mb-12">
              <div className="text-center">
                <div className="bg-card border border-border rounded-xl p-4 md:p-6 min-w-[80px] md:min-w-[100px] shadow-lg">
                  <span className="text-3xl md:text-5xl font-bold text-primary">{countdown.days}</span>
                </div>
                <span className="text-sm text-muted-foreground mt-2 block">Days</span>
              </div>
              <div className="text-center">
                <div className="bg-card border border-border rounded-xl p-4 md:p-6 min-w-[80px] md:min-w-[100px] shadow-lg">
                  <span className="text-3xl md:text-5xl font-bold text-primary">{countdown.hours}</span>
                </div>
                <span className="text-sm text-muted-foreground mt-2 block">Hours</span>
              </div>
              <div className="text-center">
                <div className="bg-card border border-border rounded-xl p-4 md:p-6 min-w-[80px] md:min-w-[100px] shadow-lg">
                  <span className="text-3xl md:text-5xl font-bold text-primary">{countdown.minutes}</span>
                </div>
                <span className="text-sm text-muted-foreground mt-2 block">Minutes</span>
              </div>
              <div className="text-center">
                <div className="bg-card border border-border rounded-xl p-4 md:p-6 min-w-[80px] md:min-w-[100px] shadow-lg">
                  <span className="text-3xl md:text-5xl font-bold text-primary">{countdown.seconds}</span>
                </div>
                <span className="text-sm text-muted-foreground mt-2 block">Seconds</span>
              </div>
            </div>

            {/* Upcoming Graduations List */}
            <div className="max-w-3xl mx-auto">
              <h3 className="text-xl font-semibold mb-4 text-center">Upcoming BMT Graduation Dates</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {upcomingGraduations
                  .filter(g => g.date > new Date())
                  .slice(0, 6)
                  .map((graduation, index) => {
                    const daysUntil = Math.ceil((graduation.date.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                    return (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          index === 0 ? 'bg-primary/10 border-primary' : 'bg-card border-border'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Calendar className={`h-5 w-5 ${index === 0 ? 'text-primary' : 'text-muted-foreground'}`} />
                          <div>
                            <p className="font-medium">
                              {graduation.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                            <p className="text-xs text-muted-foreground">{graduation.week}</p>
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${index === 0 ? 'text-primary' : 'text-muted-foreground'}`}>
                          {daysUntil} days
                        </span>
                      </div>
                    );
                  })}
              </div>
              <p className="text-center text-sm text-muted-foreground mt-4">
                * Graduation dates subject to change. Verify with official Lackland AFB announcements.
              </p>
            </div>

            <div className="text-center mt-8 space-y-4">
              <Button asChild size="lg">
                <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Your Graduation Transportation</a>
              </Button>
              <div className="pt-2">
                <Link to="/blog/what-is-jbsa-access-guide" className="text-primary hover:underline font-medium inline-flex items-center gap-2">
                  Learn About JBSA Access Requirements
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Graduation Schedule Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">BMT Graduation Weekend Schedule</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Know what to expect during your Airman's graduation weekend at Lackland AFB
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Thursday */}
              <Card className="border-primary/20">
                <CardHeader className="bg-primary/5">
                  <CardTitle className="flex items-center gap-3">
                    <Calendar className="h-6 w-6 text-primary" />
                    Thursday Events
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Airman's Run</p>
                      <p className="text-sm text-muted-foreground">Early morning run marking the transition from trainee to Airman</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Coin Ceremony</p>
                      <p className="text-sm text-muted-foreground">Airmen receive their Air Force coin from family members</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Base Liberty & Town Pass</p>
                      <p className="text-sm text-muted-foreground">Afternoon/evening time with your Airman on or off base</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Friday */}
              <Card className="border-primary/20">
                <CardHeader className="bg-primary/5">
                  <CardTitle className="flex items-center gap-3">
                    <GraduationCap className="h-6 w-6 text-primary" />
                    Friday - Graduation Day
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Graduation Parade</p>
                      <p className="text-sm text-muted-foreground">Morning parade at the Lackland Parade Grounds</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Graduation Ceremony</p>
                      <p className="text-sm text-muted-foreground">Official ceremony following the parade</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                    <div>
                      <p className="font-semibold">Base Liberty</p>
                      <p className="text-sm text-muted-foreground">Additional time with your Airman after graduation</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-8">
              <p className="text-sm text-muted-foreground">
                * Schedule subject to change. We monitor official Lackland AFB announcements and will contact you with any updates.
              </p>
            </div>
          </div>
        </section>

        {/* Transportation Packages */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">BMT Graduation Transportation Packages</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Transparent pricing based on distance & time. 20% gratuity always included. No hidden fees.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {/* Airport Package */}
              <Card className="relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs px-3 py-1 rounded-bl-lg font-semibold">
                  MOST POPULAR
                </div>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    SAT Airport
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-2xl font-bold text-primary">~18 miles</span>
                    <p className="text-sm text-muted-foreground">to Lackland AFB</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Direct base access
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Flight monitoring
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Meet & greet service
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/quick-quote">Get Instant Quote</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Downtown Package */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Downtown/Riverwalk
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-2xl font-bold text-primary">~12 miles</span>
                    <p className="text-sm text-muted-foreground">to Lackland AFB</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Direct base access
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Hotel pickup available
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Flexible timing
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild className="w-full">
                    <Link to="/quick-quote">Get Instant Quote</Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Hourly Package */}
              <Card className="border-primary">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-primary" />
                    Full Day Service
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-4">
                    <span className="text-4xl font-bold text-primary">$85</span>
                    <span className="text-muted-foreground">/hour</span>
                    <p className="text-sm text-muted-foreground">3-hour minimum</p>
                  </div>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Dedicated chauffeur
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Multiple stops included
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Wait time included
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      Gratuity included
                    </li>
                  </ul>
                  <Button asChild variant="outline" className="w-full">
                    <a href="https://ricksblackcarservicesantonio.square.site/" target="_blank" rel="noopener noreferrer">Book Hourly Service</a>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Pricing Disclaimer */}
            <div className="mt-6 p-4 bg-muted/50 rounded-lg border border-border/50 max-w-5xl mx-auto">
              <p className="text-sm text-muted-foreground text-center">
                <span className="font-medium text-foreground">Note:</span> Fares shown are estimates and calculated dynamically based on distance and time. 
                Actual fare may vary slightly depending on your exact pickup location. Use our{' '}
                <Link to="/request-quote" className="text-primary hover:underline font-medium">Instant Quote Calculator</Link>{' '}
                for a precise quote.
              </p>
            </div>

            <div className="text-center mt-8">
              <p className="text-muted-foreground mb-4">Need transportation for a larger group?</p>
              <Button asChild variant="outline">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-4 w-4" />
                  Call for Group Rates
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* What to Expect */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">What to Expect</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Your BMT Graduation transportation experience with Rick's Black Car Service
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary font-bold text-xl">1</span>
                  </div>
                  <h3 className="font-semibold mb-2">Book & Confirm</h3>
                  <p className="text-sm text-muted-foreground">
                    Provide passenger names and DOBs for security clearance processing
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary font-bold text-xl">2</span>
                  </div>
                  <h3 className="font-semibold mb-2">Day-Before Reminder</h3>
                  <p className="text-sm text-muted-foreground">
                    Receive confirmation call with chauffeur details and pickup time
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary font-bold text-xl">3</span>
                  </div>
                  <h3 className="font-semibold mb-2">Luxury Pickup</h3>
                  <p className="text-sm text-muted-foreground">
                    Your credentialed chauffeur arrives in a luxury Audi vehicle
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary font-bold text-xl">4</span>
                  </div>
                  <h3 className="font-semibold mb-2">Direct Drop-Off</h3>
                  <p className="text-sm text-muted-foreground">
                    Smooth gate entry and drop-off right at the ceremony location
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Important Information */}
        <section className="py-16 bg-card">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold mb-8 text-center">Important Information for Visitors</h2>
              
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="h-5 w-5 text-primary" />
                      Required Documentation
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      All passengers over 16 must present valid government-issued photo ID at the gate:
                    </p>
                    <ul className="grid grid-cols-2 gap-2 text-sm">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        Driver's License
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        State ID Card
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        Passport
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        Military ID (CAC)
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Clock className="h-5 w-5 text-primary" />
                      Timing Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Plan to arrive 45-60 minutes before scheduled events</li>
                      <li>• Security screenings may add time to your journey</li>
                      <li>• Your chauffeur will factor this into your pickup time</li>
                      <li>• Consider hourly service for maximum flexibility</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Shield className="h-5 w-5 text-primary" />
                      Base Entry Guidelines
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li>• Follow all instructions from Security Forces personnel</li>
                      <li>• Dress appropriately for a military installation</li>
                      <li>• Some items may be restricted through security</li>
                      <li>• Photography restrictions may apply in certain areas</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <ServiceFAQ 
              title="BMT Graduation Transportation FAQs"
              faqs={faqs}
            />
            
            {/* Blog Resource Link */}
            <div className="mt-12 max-w-2xl mx-auto p-6 bg-primary/5 border border-primary/20 rounded-xl text-center">
              <h3 className="text-lg font-semibold mb-2">Complete JBSA Transportation Guide</h3>
              <p className="text-muted-foreground mb-4">
                Learn everything about JBSA military base access, credentials, and transportation requirements.
              </p>
              <Button asChild variant="outline">
                <Link to="/blog/what-is-jbsa-access-guide" className="inline-flex items-center gap-2">
                  Read the Full Guide
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Make Their Graduation Day Special
            </h2>
            <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              Don't let transportation stress overshadow this milestone moment. Book your BMT Graduation transportation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary" className="text-lg">
                <Link to="/request-quote">Request a Quote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <a href="tel:+12102967409">
                  <Phone className="mr-2 h-5 w-5" />
                  (210) 296-7409
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Instant Quote CTA */}
        <InstantQuoteCTA />
      </main>

      <Footer />
    </>
  );
};

export default BMTGraduation;
